import { Component, Inject, Input, computed, effect, signal } from '@angular/core'
import { LngLat, MapGeoJSONFeature } from 'maplibre-gl'
import { SimulationMetrics, getMetricFromMap } from '../../../enums/simulation-metrics.enum'
import { AuthService } from '../../../../core/services/auth.service'
import { AppConfigModel } from 'src/app/core/models'
import { APP_CONFIG } from 'src/app/app.config'
import { AreaSimWarningDataModel } from 'src/app/shared/models/sim-warning-creation.model'
import { MapService } from 'src/app/core/services/map.service'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { firstValueFrom } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AppPaths } from 'src/app/app-routing.module'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { Feature, Geometry } from 'geojson'
import * as h3 from 'h3-js'
import { Permissions } from 'src/app/core/enums/permissions.enum'
import { DataSimulationWarningService } from 'src/app/shared/services/data-simulation-warning.service'
import { ToastService } from 'src/app/shared/services/toast.service'
import { LayerSimulationWarningService } from 'src/app/shared/layers/layer-simulation-warning.service'
import { SimulationWarningsEditorDataService } from '../../../services/simulation-warnings-editor-data.service'

@Component({
  selector: 'app-simulation-create-area-warnings-details',
  templateUrl: './simulation-create-area-warnings-details.component.html',
  styleUrls: ['./simulation-create-area-warnings-details.component.scss'],
  standalone: false,
})
export class SimulationCreateAreaWarningsDetailsComponent {
  public allowedTargets: any = []
  private readonly _featureDetails = signal<Map<string, DetailsModel>>(
    new Map<string, DetailsModel>(),
  )
  public readonly featureDetails = this._featureDetails.asReadonly()
  public hasFeatureDetails = computed(() => this.featureDetails().size > 0)
  public metrics = SimulationMetrics
  public selectedWarningTypeMetric = this.metrics.Warnings_LowVisibility.toString()
  public getMetricFromMap = getMetricFromMap
  private feature: Feature<Geometry, AreaSimWarningDataModel> | undefined
  public duration: number = 15
  public isLoading: boolean = false
  public lat: number = 0
  public lng: number = 0
  public bearing = ''
  public point: any
  public refreshIntervalId: NodeJS.Timeout | undefined
  public disableButton: boolean = true
  public Permissions = Permissions
  public isLVLayerVisible: boolean | undefined
  public isHRLayerVisible: boolean | undefined
  public isHSLayerVisible: boolean | undefined
  public isSWLayerVisible: boolean | undefined
  public isTLLayerVisible: boolean | undefined
  public Layers = Layers
  public confidence: number = 1.0
  public dfrs: 'DFRS_NONE' | 'DFRS_ONLY' | 'DFRS_PARTLY' = 'DFRS_NONE'
  public isAdvancedSettingsExpanded: boolean = false
  @Input() isSimulationEditorVisible: boolean = false

  constructor(
    private readonly mapEventService: MapEventsService,
    private readonly mapService: MapService,
    private readonly authService: AuthService,
    public appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
    private dataService: DataSimulationWarningService,
    private simulationTestLayer: LayerSimulationWarningService,
    private toastService: ToastService,
    private editorService: SimulationWarningsEditorDataService,
  ) {
    const backendScopes: any = this.authService.getUserClaims().userBackendScopes

    if (backendScopes) {
      backendScopes
        .toString()
        .split(',')
        .forEach((scope: string) => {
          this.allowedTargets.push({ name: scope, selected: false })
        })
      this.updateButtonState() // Use the new method
    }

    this.appState.stateChanged.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.feature) {
        this.evaluateQueryParameters()
      }
    })
    this.evaluateQueryParameters()

    effect(() => {
      const feature = this.mapEventService.clickedFeatures()
      this._featureDetails.set(new Map<string, DetailsModel>())
      if (feature[0]?.layer?.id === Layers.CreateSimAreaWarnings.name) {
        this.mapService.removeMarker()
        this.appState.setState(AppPaths.Simulation, {
          selectedFeatureId: undefined,
        })
        this.feature = feature[0] as unknown as Feature<Geometry, AreaSimWarningDataModel>
        this.appState.setState(AppPaths.Simulation, {
          selectedFeatureId: feature[0].id?.toString() ?? '',
        })
        let point = h3.cellToLatLng(this.feature?.properties.h3id ?? '')
        this.mapService.setMarker(
          {
            lng: point[1],
            lat: point[0],
          } as LngLat,
          `${this.selectedWarningTypeMetric}-marker`,
        )
        this.updateAreaDetail()
      }
    })
  }

  updateAreaDetail() {
    this.setFeatureDetail('Hexagon', {
      title: 'Hexagon',
      items: [
        {
          description: 'Hex ID',
          value: this.feature?.properties.h3id ?? '',
        },
        {
          description: 'Country Code',
          value: this.feature?.properties.countryCode ?? '',
        },
      ],
    })
  }

  close() {
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
    })
    this.mapService.removeMarker()
    this._featureDetails.set(new Map<string, DetailsModel>())
    this.appState.setState(AppPaths.Simulation, {
      selectedFeatureId: undefined,
    })
  }

  private setFeatureDetail(key: string, value: DetailsModel): void {
    this._featureDetails.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  setMetric(warningTypeMetric: string) {
    this.selectedWarningTypeMetric = warningTypeMetric
    this.mapService.changeMarker(`${this.selectedWarningTypeMetric}-marker`)
    // Force change detection for Angular 21
  }

  async createWarning() {
    const LatLng = h3.cellToLatLng(this.feature?.properties.h3id ?? '')
    let newWarning: AreaSimWarningDataModel = {
      warningName: getMetricFromMap(this.selectedWarningTypeMetric).name,
      latitude: LatLng[0],
      longitude: LatLng[1],
      ttl: this.duration * 60000,
      isCancelled: false,
      // Only include confidence if advanced settings are expanded
      ...(this.isAdvancedSettingsExpanded && {
        confidence: this.confidence,
      }),
      dfrsClassification: this.isAdvancedSettingsExpanded ? this.dfrs : 'DFRS_NONE',
      targets: this.allowedTargets
        .filter((target: any) => target.selected)
        .map((target: any) => target.name),
      countryCode: this.feature?.properties.countryCode ?? '',
      h3id: this.feature?.properties.h3id ?? '',
    }
    this.isLoading = true
    if (this.isSimulationEditorVisible) {
      // vector tile geometry is zoom-simplified/clipped, so always derive the exact hexagon from the h3id
      const polygonCoords = [h3.cellToBoundary(this.feature?.properties.h3id ?? '', true)]

      this.editorService.addWarning({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: polygonCoords },
        properties: newWarning,
      })
      this.toastService.show({ type: 'success', message: 'Warning added to editor' })
      this.isLoading = false
      this.close()
    } else {
      try {
        const result: any = await firstValueFrom(
          this.dataService.createAreaTestWarnings(newWarning),
        )
        this.toastService.show({ type: 'success', message: 'Warning creation initialized' })
        this.close()
        clearInterval(this.refreshIntervalId)
        this.refreshIntervalId = setInterval(() => this.simulationTestLayer.reload(), 2000)

        setTimeout(
          (function (id_local) {
            return function () {
              clearInterval(id_local)
            }
          })(this.refreshIntervalId),
          15000,
        )
      } catch (error) {
        const status = error instanceof HttpErrorResponse ? ` (${error.status})` : ''
        this.toastService.show({ type: 'error', message: `Error creating warning${status}` })
      } finally {
        this.isLoading = false
      }
    }
  }

  ngOnDestroy() {
    this.mapService.removeMarker()
  }

  evaluateQueryParameters() {
    // Set the selected warning type marker based on the layers that are visible
    const layers = this.appState.getState().layers
    this.isLVLayerVisible = layers?.includes(Layers.LVWarning.name)
    this.isHRLayerVisible = layers?.includes(Layers.HRWarning.name)
    this.isHSLayerVisible = layers?.includes(Layers.HSWarning.name)
    this.isSWLayerVisible = layers?.includes(Layers.SWWarning.name)
    this.isTLLayerVisible = layers?.includes(Layers.TLWarning.name)
    if (
      (this.selectedWarningTypeMetric == this.metrics.Warnings_LowVisibility.toString() &&
        !this.isLVLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_HeavyRain.toString() &&
        !this.isHRLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_HeavySnow.toString() &&
        !this.isHSLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_StrongWind.toString() &&
        !this.isSWLayerVisible) ||
      (this.selectedWarningTypeMetric == this.metrics.Warnings_TractionLoss.toString() &&
        !this.isTLLayerVisible) ||
      this.selectedWarningTypeMetric == ''
    ) {
      if (this.isLVLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_LowVisibility.toString()
      } else if (this.isHRLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_HeavyRain.toString()
      } else if (this.isHSLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_HeavySnow.toString()
      } else if (this.isSWLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_StrongWind.toString()
      } else if (this.isTLLayerVisible) {
        this.selectedWarningTypeMetric = this.metrics.Warnings_TractionLoss.toString()
      } else {
        this.selectedWarningTypeMetric = ''
      }
      if (this.feature) {
        this.mapService.changeMarker(`${this.selectedWarningTypeMetric}-marker`)
      }
    }
  }

  validateScopes(target: any) {
    target.selected = !target.selected
    this.updateButtonState()
    // Force change detection for Angular 21
  }

  updateTargetSelection(checked: boolean, target: any): void {
    target.selected = checked
    this.updateButtonState()
    // Force change detection for Angular 21
  }

  private updateButtonState(): void {
    this.allowedTargets.filter((target: any) => target.selected).length > 0
      ? (this.disableButton = false)
      : (this.disableButton = true)
  }
}
